import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ShieldCheck, CheckCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      navigate("/dashboard");
      return;
    }

    pollPaymentStatus(0);
  }, [sessionId]);

  const pollPaymentStatus = async (attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_URL}/api/payments/status/${sessionId}`,
        { withCredentials: true }
      );

      if (data.payment_status === "paid") {
        setStatus("success");
        setPaymentData(data);
        return;
      } else if (data.status === "expired") {
        setStatus("error");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans']">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-16 sm:py-24">
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 text-center" data-testid="payment-success-card">
            {status === "checking" && (
              <>
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-[#19294C] font-['Outfit']">
                  Vérification du paiement...
                </h1>
                <p className="mt-4 text-slate-600">
                  Veuillez patienter pendant que nous confirmons votre paiement.
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-20 h-20 bg-[#00B8E4]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-[#00B8E4]" />
                </div>
                <h1 className="text-2xl font-bold text-[#19294C] font-['Outfit']" data-testid="success-title">
                  Paiement réussi !
                </h1>
                <p className="mt-4 text-slate-600">
                  Merci pour votre confiance. Votre protection HostProtect est maintenant active.
                </p>

                {paymentData && (
                  <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Montant payé</p>
                    <p className="text-2xl font-bold text-[#19294C]">
                      {formatPrice(paymentData.amount)}
                    </p>
                  </div>
                )}

                <div className="mt-8 space-y-4">
                  <h3 className="text-lg font-semibold text-[#19294C] font-['Outfit']">
                    Prochaines étapes
                  </h3>
                  <ul className="text-left space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#00B8E4]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[#00B8E4] text-sm font-bold">1</span>
                      </div>
                      <span className="text-slate-600">Vérifiez votre email pour le récapitulatif</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#00B8E4]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[#00B8E4] text-sm font-bold">2</span>
                      </div>
                      <span className="text-slate-600">Complétez vos informations de logement</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#00B8E4]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[#00B8E4] text-sm font-bold">3</span>
                      </div>
                      <span className="text-slate-600">Votre protection sera active sous 24h</span>
                    </li>
                  </ul>
                </div>

                <Link to="/dashboard" className="block mt-8">
                  <Button 
                    className="w-full bg-[#00B8E4] hover:bg-[#00a5ce] text-white"
                    data-testid="go-to-dashboard-btn"
                  >
                    Accéder à mon espace
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </>
            )}

            {status === "error" && (
              <>
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ShieldCheck className="w-10 h-10 text-yellow-600" />
                </div>
                <h1 className="text-2xl font-bold text-[#19294C] font-['Outfit']">
                  Vérification en cours
                </h1>
                <p className="mt-4 text-slate-600">
                  Nous n'avons pas pu confirmer votre paiement immédiatement. 
                  Vérifiez votre email pour la confirmation ou contactez-nous.
                </p>
                <Link to="/dashboard" className="block mt-8">
                  <Button className="w-full bg-[#00B8E4] hover:bg-[#00a5ce] text-white">
                    Retour au tableau de bord
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PaymentSuccessPage;
